import { NetworkStatus } from '@apollo/client';
import { useEffect } from 'react';

import { variablesAtLoadedDepth } from '@utils/loadedPageDepth';

import type { ObservableQuery, OperationVariables } from '@apollo/client';

/**
 * One poll of `observableQuery` at the depth it has loaded: a network-only
 * (or no-cache) fetch that leaves the query's own options alone. Returns
 * false, without fetching, while a request for the query is already in
 * flight — same as Apollo's own poll.
 */
export const refreshAtLoadedDepth = <
  TData,
  TVariables extends OperationVariables,
>(
  observableQuery: ObservableQuery<TData, TVariables>
): boolean => {
  // Apollo's isNetworkRequestInFlight: every status below `ready` (7).
  if (
    observableQuery.getCurrentResult(false).networkStatus < NetworkStatus.ready
  ) {
    return false;
  }
  observableQuery
    .reobserve(
      {
        fetchPolicy:
          observableQuery.options.fetchPolicy === 'no-cache'
            ? 'no-cache'
            : 'network-only',
        variables: variablesAtLoadedDepth(observableQuery),
      },
      NetworkStatus.poll
    )
    // Same as Apollo's poll: a failure reaches the query's own result
    // (and errorLink); the next refresh tries again.
    .catch(() => undefined);
  return true;
};

/**
 * `pollInterval` for a query paginated with `fetchMore`, without dropping the
 * pages already loaded: each poll asks for as many items as are loaded (see
 * variablesAtLoadedDepth). Apollo's own `pollInterval` re-runs the query with
 * its original one-page variables, and `relayStylePagination` then replaces
 * every loaded page with page 1 (TA-11016).
 *
 * Mirrors Apollo's poll otherwise: a network-only (or no-cache) fetch that
 * leaves the query's own options alone, skipped while a request for the
 * query is already in flight.
 */
export const usePollAtLoadedDepth = <
  TData,
  TVariables extends OperationVariables,
>(
  observableQuery: ObservableQuery<TData, TVariables> | undefined,
  pollInterval: number | undefined,
  skip = false
) => {
  useEffect(() => {
    if (!observableQuery || !pollInterval || skip) return undefined;
    const timer = setInterval(() => {
      refreshAtLoadedDepth(observableQuery);
    }, pollInterval);
    return () => clearInterval(timer);
  }, [observableQuery, pollInterval, skip]);
};
